// assets/AllCourses/Exercises/CategorizingWordsExercise.jsx

import React, { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import { cn } from "@/utils/utils";
import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function CategorizingWordsExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE DATA ================= */
  const categories = Array.isArray(exercise.categories)
    ? exercise.categories.filter(Boolean)
    : [];

  const words = Array.isArray(exercise.words)
    ? exercise.words.filter(
        (w) => w?.word?.trim() && w?.category?.trim()
      )
    : [];

  /* ================= ENGINE QUESTIONS ================= */
  const engineQuestions = words.map((w) => ({
    type: "categorize",
    answer: w.category,
    skill: "categorization",
  }));

  /* ================= SAFE EXTERNAL ANSWERS ================= */
  const safeExternalAnswers =
    typeof userAnswer?.answer === "object" ? userAnswer.answer : {};

  /* ================= ENGINE ================= */
  const { answers, setAnswer } = useExerciseEngine({
    questions: engineQuestions,
    showResult: showResults,
    externalAnswers: safeExternalAnswers,
  });

  /* ================= SYNC WITH PARENT ================= */
  const prevRef = useRef({});

  useEffect(() => {
    if (isPreview) return;

    const isSame =
      JSON.stringify(prevRef.current) === JSON.stringify(answers);

    if (isSame) return;

    prevRef.current = answers;

    onComplete({
      answer: answers,
    });
  }, [answers, isPreview, onComplete]);

  /* ================= WORD BANK ================= */
  const [wordBank, setWordBank] = useState([]);

  useEffect(() => {
    if (!exercise?._id) return;

    const allWords = words.map((w) => w.word);

    const usedIndexes = Object.keys(answers || {}).map(Number);

    const usedWords = usedIndexes
      .map((idx) => words[idx]?.word)
      .filter(Boolean);

    const remaining = allWords.filter(
      (w) => !usedWords.includes(w)
    );

    setWordBank(remaining);
  }, [exercise._id, words, answers]);

  /* ================= DROP ================= */
  const handleDrop = (e, category) => {
    if (isPreview || showResults) return;

    e.preventDefault();
    const word = e.dataTransfer.getData("text/plain");

    const index = words.findIndex((w) => w.word === word);
    if (index === -1) return;

    setAnswer(index, category);
  };

  /* ================= REMOVE ================= */
  const handleRemove = (word) => {
    if (showResults) return;

    const index = words.findIndex((w) => w.word === word);
    if (index === -1) return;

    setAnswer(index, undefined); // ✅ IMPORTANT FIX
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-10">
        {/* Categories */}
        <div className="space-y-3">
          <p className="font-medium text-sm">Categories</p>

          {categories.map((cat, i) => (
            <div key={i} className="flex gap-3">
              <StringField
                value={cat}
                onChange={(v) => {
                  const next = [...categories];
                  next[i] = v;
                  onUpdate("categories", next);
                }}
              />
              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  onUpdate(
                    "categories",
                    categories.filter((_, idx) => idx !== i)
                  )
                }
              >
                Remove
              </Button>
            </div>
          ))}

          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onUpdate("categories", [...categories, ""])
            }
          >
            Add Category
          </Button>
        </div>

        {/* Words */}
        <div className="space-y-3">
          <p className="font-medium text-sm">Words</p>

          {words.map((w, i) => (
            <div key={i} className="flex gap-3">
              <StringField
                value={w.word}
                onChange={(v) => {
                  const next = [...words];
                  next[i] = { ...next[i], word: v };
                  onUpdate("words", next);
                }}
              />

              <select
                value={w.category}
                onChange={(e) => {
                  const next = [...words];
                  next[i] = {
                    ...next[i],
                    category: e.target.value,
                  };
                  onUpdate("words", next);
                }}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="">Category</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  onUpdate(
                    "words",
                    words.filter((_, idx) => idx !== i)
                  )
                }
              >
                Remove
              </Button>
            </div>
          ))}

          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onUpdate("words", [
                ...words,
                { word: "", category: "" },
              ])
            }
          >
            Add Word
          </Button>
        </div>
      </div>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle>Categorize the Words</CardTitle>
        <CardDescription>
          Drag each word into the correct category
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* WORD BANK */}
        <div className="rounded-xl border bg-muted/40 p-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {wordBank.map((word) => (
              <Button
                key={word}
                draggable={!showResults}
                size="sm"
                variant="secondary"
                onDragStart={(e) =>
                  e.dataTransfer.setData("text/plain", word)
                }
              >
                {word}
              </Button>
            ))}
          </div>
        </div>

        {/* CATEGORIES */}
        <div className="grid md:grid-cols-2 gap-6">
          {categories.map((cat) => (
            <div
              key={cat}
              onDrop={(e) => handleDrop(e, cat)}
              onDragOver={(e) => e.preventDefault()}
              className="rounded-xl border-2 border-dashed p-5 min-h-[180px]"
            >
              <h3 className="text-center font-semibold mb-4">
                {cat}
              </h3>

              <div className="space-y-2">
                {words.map((w, idx) => {
                  if (answers[idx] !== cat) return null;

                  const correct = w.category === cat;

                  return (
                    <div
                      key={w.word}
                      onClick={() => handleRemove(w.word)}
                      className={cn(
                        "p-3 rounded text-sm text-center font-medium cursor-pointer",
                        showResults &&
                          correct &&
                          "bg-green-100 border-green-400",
                        showResults &&
                          !correct &&
                          "bg-red-100 border-red-400",
                        !showResults && "bg-muted hover:bg-muted/70"
                      )}
                    >
                      {w.word}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}